import type { HiredEquipmentAnchor, HiredEquipmentEntryMethod } from '../types';
import { formatPoOptionLabel } from './poDisplay';

export interface BuildAnchorInput {
  entryMethod: HiredEquipmentEntryMethod;
  vendorId?: number | null;
  vendorName?: string | null;
  poId?: number | null;
  poNumber?: string | null;
  poDescription?: string | null;
  taskId?: number | null;
  taskLabel?: string | null;
  serviceAreaId?: number | null;
  prePopulate: boolean;
  poEnabled: boolean;
}

/** Snapshot the chooser into the immutable grid payload. */
export function buildHiredEquipmentAnchor(input: BuildAnchorInput): HiredEquipmentAnchor {
  const {
    entryMethod,
    vendorId,
    vendorName,
    poId,
    poNumber,
    poDescription,
    taskId,
    taskLabel,
    serviceAreaId,
    prePopulate,
    poEnabled,
  } = input;

  const anchor: HiredEquipmentAnchor = {
    entryMethod,
    serviceAreaId: serviceAreaId ?? null,
  };

  if (entryMethod === 'purchase_order' || entryMethod === 'subcontractor') {
    if (vendorId) {
      anchor.lockedVendorId = vendorId;
      if (vendorName) anchor.lockedVendorName = vendorName;
    }
  }

  if (entryMethod === 'purchase_order' && poEnabled && poId) {
    anchor.lockedPoId = poId;
    if (poNumber) anchor.lockedPoNumber = poNumber;
    if (poDescription) anchor.lockedPoDescription = poDescription;
    anchor.prePopulate = prePopulate;
  } else if (entryMethod === 'purchase_order') {
    anchor.prePopulate = false;
  }

  if (entryMethod === 'task' && taskId) {
    anchor.lockedTaskId = taskId;
    if (taskLabel) anchor.lockedTaskLabel = taskLabel;
  }

  return anchor;
}

export function methodLabel(method: HiredEquipmentEntryMethod): string {
  switch (method) {
    case 'purchase_order':
      return 'By Purchase Order';
    case 'subcontractor':
      return 'By Subcontractor';
    case 'task':
      return 'By Task';
  }
}

export function formatAnchorValue(anchor: HiredEquipmentAnchor): string {
  switch (anchor.entryMethod) {
    case 'purchase_order': {
      const po = anchor.lockedPoNumber
        ? formatPoOptionLabel({ poNumber: anchor.lockedPoNumber, description: anchor.lockedPoDescription })
        : anchor.lockedPoId != null
          ? `PO #${anchor.lockedPoId}`
          : null;
      const vendor = anchor.lockedVendorName || (anchor.lockedVendorId != null ? `Vendor #${anchor.lockedVendorId}` : null);
      return [po, vendor].filter(Boolean).join(' · ') || 'Purchase Order';
    }
    case 'subcontractor':
      return anchor.lockedVendorName || (anchor.lockedVendorId != null ? `Vendor #${anchor.lockedVendorId}` : 'Subcontractor');
    case 'task':
      return anchor.lockedTaskLabel || (anchor.lockedTaskId != null ? `Task #${anchor.lockedTaskId}` : 'Task');
  }
}

/** Page header for the grid: PO number is the title when the submission is anchored to a PO. */
export function hiredEquipmentGridPageTitle(anchor: HiredEquipmentAnchor): string {
  const po = anchor.lockedPoNumber?.trim();
  if (anchor.entryMethod === 'purchase_order' && po) {
    return `New Hired Equipment — ${po}`;
  }
  return 'New Hired Equipment Submission';
}
